import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Typography,
  Divider,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Modal,
  IconButton,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MenuSection from "../components/MenuSection";
import iconoCajero from "../images/icono-cajero.png";
import CloseIcon from "@mui/icons-material/Close";
import transferenciasAPI from "../api/transferencias";
import { verificarEmpleado } from "../helpers/verificarEmpleado";
import { generarComprobanteTransferencia, inicializarCanvas } from "../helpers/facturaUtils";
import apiErrorHandler from "../helpers/apiErrorHandler"; // Importa el helper
import proximoNumeroSobre from "../api/proximoNumeroSobre"; // Importa la API para obtener el próximo número de sobre
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import existeNumeroSobre from "../api/existeNumeroSobre"; // Importa la API para verificar si el número de sobre existe

const TransferenciaPage = () => {
  const esDUO = JSON.parse(localStorage.getItem("esDUO")) || false;
  const [modalOpen, setModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [nombreCaja, setNombreCaja] = useState(JSON.parse(localStorage.getItem("puesto")).nombre);
  const [efectivo, setEfectivo] = useState("");
  const [totalTransferido, setTotalTransferido] = useState(0);
  const [transferencias, setTransferencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [selectedOption, setSelectedOption] = useState("entregadoTesoreria"); // Default selected option
  const [TipoTransferencia, setTipoTransferencia] = useState(1); // Estado para TipoTransferencia
  const [nombreTransferencia, setNombreTransferencia] = useState("Tesoreria"); // Estado para TipoTransferencia
  const [numeroSobre, setNumeroSobre] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const colorTexto = theme.bandera?.colorTextoBoton || "white";
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString("es-AR");
  const formattedTime = currentDate.toLocaleTimeString("es-AR");
  const estacionData = JSON.parse(localStorage.getItem("estacionData"));
  const puesto = JSON.parse(localStorage.getItem("puesto"));
  const [isEditandoSobre, setIsEditandoSobre] = useState(false);
  const configuracionTesoreria = localStorage.getItem("configuracionTesoreria");

  useEffect(() => {
    const fetchTransferencias = async () => {
      setLoading(true);
      try {
        const ultimasTransferencias = await transferenciasAPI.getTransferencias();

        const nroSobre = await proximoNumeroSobre.getProximoNumeroSobre();
        setNumeroSobre(nroSobre.proximoNumeroSobre);
        localStorage.setItem("nroSobreInicial", nroSobre.proximoNumeroSobre);

        console.log("Número de sobre obtenido:", nroSobre.proximoNumeroSobre);
        console.log("Transferencias obtenidas:", ultimasTransferencias.listadoTransferencias);

        setTransferencias(ultimasTransferencias.listadoTransferencias);
        setTotalTransferido(ultimasTransferencias.totalTrans);
      } catch (error) {
        console.error("Error al obtener las transferencias:", error);
        setTransferencias([]);
        setTotalTransferido(0);
        apiErrorHandler(error, { stopFlow: true });
      } finally {
        setLoading(false);
      }
    };

    fetchTransferencias();
  }, []);

  const handleGuardarTransferencia = async () => {
    if (isProcessing) return; // Evita múltiples envíos
    setIsProcessing(true); // Comienza el proceso de transferencia
    setLoading(true);

    try {
      const resultadoVerificacion = await verificarEmpleado(password);

      if (!resultadoVerificacion.success) {
        setPasswordError(true);
        setIsProcessing(false); // Resetea el estado de carga si hay un error
        return;
      }

      const efectivoValue = parseFloat(efectivo) || 0;

      if (TipoTransferencia === 3) {
        const existeSobre = await existeNumeroSobre.getExisteNumeroSobre(numeroSobre);
        console.log("Resultado de la verificación del número de sobre:", existeSobre);
        if (existeSobre) {
          alert("El número de sobre ya fue utilizado. Por favor, ingrese uno diferente.");
          setIsProcessing(false);
          setLoading(false);
          return;
        }
      }

      // Realizar la transferencia
      const respuestaTransferencia = await transferenciasAPI.realizarTransferencia(
        resultadoVerificacion.empleado.idEmpleado,
        efectivoValue,
        TipoTransferencia, // Pasar el valor de TipoTransferencia a la función
        TipoTransferencia === 3 ? numeroSobre : null,
      );

      console.log(respuestaTransferencia);
      // Verificar si la respuesta indica éxito
      if (respuestaTransferencia.status === 200) {
        // Obtener las últimas transferencias desde la API
        const ultimasTransferencias = await transferenciasAPI.getTransferencias();

        setTransferencias(ultimasTransferencias.listadoTransferencias);
        setTotalTransferido(ultimasTransferencias.totalTrans);

        // Inicializar el canvas y contexto antes de usarlo
        const context = inicializarCanvas(550, 650); // Ancho 550 y alto 100
        let y = 30; // Valor inicial de y para dibujar en el canvas

        const nroComprobante = respuestaTransferencia.data;
        // Generar el comprobante de transferencia
        generarComprobanteTransferencia(
          context,
          y,
          estacionData.data,
          formattedDate,
          formattedTime,
          puesto,
          nombreTransferencia,
          efectivoValue,
          resultadoVerificacion.empleado.nombreEmpleado,
          nroComprobante,
        );

        const dataURL = context.canvas.toDataURL("image/png");
        const base64Data = dataURL.replace(/^data:image\/png;base64,/, "");
        console.log(base64Data);

        // Imprimir solo si la transferencia fue exitosa
       window.MyInterface.imprimirComprobanteMercadoPago(base64Data);

        // Limpiar el estado después de la transferencia
        setEfectivo("");
        setSnackbarOpen(true);
        setPasswordModalOpen(false);

        try {
          const nroSobre = await proximoNumeroSobre.getProximoNumeroSobre();
          setNumeroSobre(nroSobre.proximoNumeroSobre);
          localStorage.setItem("nroSobreInicial", nroSobre.proximoNumeroSobre);
          console.log("Nuevo número de sobre actualizado:", nroSobre.proximoNumeroSobre);
        } catch (error) {
          console.error("Error al actualizar el número de sobre:", error);
        }
      } else {
        console.error("Error en la transferencia:", respuestaTransferencia);
      }
    } catch (error) {
      console.error("Error al realizar la transferencia:", error); // Capturar y loguear cualquier error
      apiErrorHandler(error, { stopFlow: true });
    } finally {
      setLoading(false); // Termina la carga
      setIsProcessing(false); // Finaliza el proceso
      setPasswordModalOpen(false); // Cerrar el modal de contraseña al terminar
    }
  };

  const handlePasswordSubmit = () => {
    handleGuardarTransferencia();
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => setModalOpen(false);
  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const openPasswordModal = () => {
    setPassword("");
    setPasswordError(false);
    setPasswordModalOpen(true);
  };

  const handleTipoTransferenciaChange = event => {
    const value = event.target.value;

    switch (value) {
      case "entregadoTesoreria":
        setTipoTransferencia(1);
        setNombreTransferencia("Tesoreria");
        break;
      case "recibidoTesoreria":
        setTipoTransferencia(2);
        setNombreTransferencia("");
        break;
      case "entregadoEmpresa":
        setTipoTransferencia(3);
        setNombreTransferencia("Empresa");
        break;
      default:
        setNombreTransferencia("Tesoreria");
        setTipoTransferencia(1);
    }

    setSelectedOption(value);
  };

  const handleAbrirScanner = () => {
    window.MyInterface?.scanMercadoPago?.();
  };

  return (
    <>
      <MenuSection />

      <Box sx={{ width: "90%", height: window.innerWidth >= 600 ? "68vh" : "80vh", marginLeft: window.innerWidth >= 600 ? esDUO? 25 : 20 : 0 }}>
        <Typography sx={{ fontWeight: "600", fontSize: window.innerWidth < 400 ? "14px" : esDUO? "20px" :"16px", marginBottom: "8px" }}>
          Transferencias de valores durante el turno
        </Typography>
        <Divider width={window.innerWidth >= 600 ? esDUO? "85%" :"70%" : "100%"} />
        <Box sx={{ width: esDUO? "100%" : "90%", margin: "0 auto", marginTop: esDUO? "3rem" : "10px" }}>
          <Grid container spacing={2}>
            {/* Primera columna */}
            <Grid item xs={12} sm={esDUO?6:4}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontWeight: 500, fontSize: "14px", marginBottom: "8px" }}>CAJA: {nombreCaja}</Typography>

                <Typography sx={{ fontWeight: 600, margin: "8px 0 6px 0", fontSize: "14px" }}>Datos de la transferencia</Typography>

                <RadioGroup value={selectedOption} onChange={handleTipoTransferenciaChange} sx={{ marginBottom: "8px" }}>
                  <FormControlLabel
                    value="entregadoTesoreria"
                    control={<Radio sx={{ color: theme.bandera.colorSecundario, "&.Mui-checked": { color: theme.bandera.colorSecundario }, padding: "4px" }} />}
                    label={<Typography sx={{ fontSize: "14px" }}>Entregado a tesorería</Typography>}
                  />
                  <FormControlLabel
                    value="recibidoTesoreria"
                    control={<Radio sx={{ color: theme.bandera.colorSecundario, "&.Mui-checked": { color: theme.bandera.colorSecundario }, padding: "4px" }} />}
                    label={<Typography sx={{ fontSize: "14px" }}>Recibido de tesorería</Typography>}
                  />
                  <FormControlLabel
                    value="entregadoEmpresa"
                    control={
                      <Radio
                        sx={{
                          color: theme.bandera.colorSecundario,
                          "&.Mui-checked": { color: theme.bandera.colorSecundario },
                          padding: "4px",
                        }}
                      />
                    }
                    label={<Typography sx={{ fontSize: "14px" }}>Entregado a empresa recaudadora</Typography>}
                    disabled={!configuracionTesoreria || configuracionTesoreria === "false"}
                  />
                </RadioGroup>
              </Box>
            </Grid>

            {/* Segunda columna */}
            <Grid item xs={12} sm={esDUO?6:8}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ marginBottom: "8px", marginTop: "6px", fontWeight: 600, fontSize: "14px" }}>Detalle de los valores transferidos</Typography>
                <TextField
                  label="Efectivo"
                  value={efectivo}
                  onChange={e => setEfectivo(e.target.value)}
                  fullWidth
                  type="number"
                  sx={{ marginBottom: "10px" }}
                  InputProps={{
                    style: {
                      padding: "0 14px",
                      width: window.innerWidth > 700 ? "63.5%" : "100%",
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />

                {TipoTransferencia === 3 && (
                  <Box sx={{ display: "flex", width: window.innerWidth >= 600 ? "63.5%" : "100%", alignItems: "center", marginBottom: "10px" }}>
                    <TextField
                      label="Nro Sobre"
                      value={numeroSobre}
                      onChange={e => setNumeroSobre(e.target.value)}
                      fullWidth
                      type="number"
                      disabled={!isEditandoSobre}
                      error={isEditandoSobre && (!numeroSobre || parseInt(numeroSobre) === 0)}
                      helperText={isEditandoSobre && (!numeroSobre || parseInt(numeroSobre) === 0) ? "Ingrese un número mayor a cero" : ""}
                      InputProps={{
                        style: {
                          padding: "0 14px",
                          width: window.innerWidth > 700 ? "90%" : "100%",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <Button variant="outlined" size="small" onClick={() => setIsEditandoSobre(!isEditandoSobre)}>
                      {isEditandoSobre ? <LockIcon /> : <EditIcon />}
                    </Button>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    color: "#7c7d81",
                    fontWeight: "600",
                    margin: "0 auto",
                    ...(window.innerWidth > 700&&{marginLeft: "20%"}),
                    borderColor: theme.bandera.colorSecundario,
                    fontSize: "14px",
                    padding: "6px 12px",
                  }}
                  onClick={openPasswordModal}
                  startIcon={<img src={iconoCajero} alt="Icon" style={{ width: 20, height: 20 }} />}
                  disabled={
                    efectivo === "" ||
                    isNaN(Number(efectivo)) ||
                    Number(efectivo) <= 0 ||
                    (TipoTransferencia === 3 && (numeroSobre === "" || Number(numeroSobre) === 0))
                  }
                >
                  Cajero
                </Button>

                {loading ? (
                  <Skeleton animation="wave" width="100%" height={56} />
                ) : (
                  <TextField
                    label="Total transferido"
                    value={`$${totalTransferido.toFixed(2)}`}
                    InputProps={{
                      readOnly: true,
                      style: {
                        padding: "0 14px",
                        width: window.innerWidth >= 600 ? "63.5%" : "100%",
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                    sx={{ marginTop: "8px", marginBottom: "10px", position: "sticky", bottom: theme.spacing(2), backgroundColor: "#fff", zIndex: 1 }}
                  />
                )}

                <Button
                  variant="outlined"
                  onClick={handleOpenModal}
                  size="small"
                  sx={{
                    marginBottom: "8px",
                    color: theme.bandera.colorSecundario,
                    borderColor: theme.bandera.colorSecundario,
                    fontSize: "14px",
                    padding: "6px 12px",
                    width: window.innerWidth > 700 ? "63.5%" : "100%",
                  }}
                >
                  Ver Transferencias Realizadas
                </Button>

                <Modal open={modalOpen} onClose={handleCloseModal} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Box sx={{ width: "100%", maxWidth: 600, px: 4, bgcolor: "background.paper", borderRadius: 1, maxHeight: "80vh", overflowY: "auto" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        bgcolor: "background.paper",
                        p: 2,
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Transferencias realizadas</Typography>
                      <IconButton onClick={handleCloseModal}>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {loading ? (
                      <Skeleton animation="wave" width="100%" height={80} />
                    ) : (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableBody>
                            {transferencias
                              .slice()
                              .reverse()
                              .map((transferencia, index) => (
                                <TableRow key={index}>
                                  <TableCell>{transferencia}</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Modal>

                <Modal
                  open={passwordModalOpen}
                  onClose={() => setPasswordModalOpen(false)}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <Box sx={{ width: 300, p: 4, bgcolor: "background.paper", borderRadius: 1, textAlign: "center" }}>
                    <Typography sx={{ mb: 2 }}>Ingrese su contraseña</Typography>
                    <TextField
                      label="Contraseña"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter" && !isProcessing) {
                          handlePasswordSubmit();
                        }
                      }}
                      fullWidth
                      error={passwordError}
                      helperText={passwordError && "Contraseña incorrecta"}
                    />
                    <Button
                      variant="contained"
                      onClick={handlePasswordSubmit}
                      disabled={isProcessing}
                      sx={{
                        mt: 2,
                        backgroundColor: theme.bandera.colorSecundario,
                        color: colorTexto,
                        ":hover": {
                          backgroundColor: theme.bandera.colorSecundario,
                        },
                        "&.Mui-disabled": {
                          color: colorTexto,
                          backgroundColor: theme.bandera.colorSecundario,
                        },
                      }}
                    >
                      {isProcessing ? "Procesando..." : "Confirmar"}
                    </Button>
                  </Box>
                </Modal>

                <Modal
                  open={snackbarOpen}
                  onClose={handleCloseSnackbar}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0, 0, 0, 0.1)" }}
                >
                  <Box
                    sx={{
                      width: 300,
                      p: 4,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      textAlign: "center",
                      boxShadow: 24,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 60, color: "green" }} />
                    <Typography sx={{ mt: 2, fontWeight: 600, fontSize: 20 }}>Transferencia realizada con éxito</Typography>
                  </Box>
                </Modal>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default TransferenciaPage;
